import { Events } from '@/models/events.model'
import { Artifact, asSnapshotReference } from '@/models'

import GraphState from './../rappid/graph-state'

import { useRoute } from 'vue-router'
import { freezeWrap, tryWrap, useEventBus } from '@/utils/events'
import { useTraceDataStore } from '@/stores/traceDataStore'

import { parseIntUndefined } from '@/utils/helpers'
import { injectTyped } from '@/inject'
import { ServiceKeys } from '@/services/symbols'

export function useRegisterArtifactTypeContainerSearch() {
  const route = useRoute()
  const traceDataStore = useTraceDataStore()

  const rappidContext = injectTyped(ServiceKeys.rappidContext)
  const searchService = injectTyped(ServiceKeys.search)
  const artifactService = injectTyped(ServiceKeys.artifact)

  let graphState: GraphState | undefined = undefined

  async function handler(event: Events['searchArtifactsForArtifactType']): Promise<void> {
    if (!graphState || !route.params.projectId || !route.params.snapshotId) {
      throw Error('Missing data for rendering tracegraph')
    }

    if (!event.searchMode) {
      await searchInRegularMode(event)
    } else {
      await searchInSearchMode(event)
    }
  }

  async function searchInSearchMode(event: Events['searchArtifactsForArtifactType']) {
    if (event.query != '') {
      event.artifactType.containerSearchQuery = event.query

      // Remove all artifacts and corresponding links for the searched artifact type
      const artifacts = traceDataStore.getArtifactsForArtifactType(event.artifactType)
      //.filter((artifact) => !artifact.name.includes(event.query))

      const links = artifacts.flatMap((a) => traceDataStore.getLinksForArtifact(a))
      graphState?.removeArtifactsFromModel(artifacts).removeLinksFromModel(links)

      const matchingArtifacts = traceDataStore
        .getArtifactsForArtifactType(event.artifactType)
        .filter((artifact) => artifact.name.includes(event.query))

      const matchingLinks = artifacts.flatMap((a) => traceDataStore.getLinksForArtifact(a))

      graphState
        ?.addToModel({
          artifacts: matchingArtifacts,
          artifactTypes: traceDataStore.modelState.artifactTypes,
          linkTypes: traceDataStore.modelState.linkTypes,
          links: matchingLinks,
        })
        .layout()
    } else {
      graphState?.addToModel(traceDataStore.modelState).layout()
    }
  }

  async function searchInRegularMode(event: Events['searchArtifactsForArtifactType']) {
    // Remove all artifacts and corresponding links for the searched artifact type
    const artifacts = traceDataStore.getArtifactsForArtifactType(event.artifactType)
    const links = artifacts.flatMap((a) => traceDataStore.getLinksForArtifact(a))

    graphState?.removeArtifactsFromModel(artifacts).removeLinksFromModel(links)

    if (event.query != '') {
      const encodedQuery = event.query
      event.artifactType.containerSearchQuery = encodedQuery

      await traceDataStore.searchArtifactsForArtifactType(event.artifactType, {
        searchService: searchService,
        searchString: encodedQuery,
        projectId: parseInt(route.params.projectId as string),
        snapshotId: asSnapshotReference(route.params.snapshotId),
        filterId: parseIntUndefined(route.query.filter as string),
        page: 1,
        itemsPerPage: 4,
      })

      // Get the links for the resulting artifacts
      await Promise.all(
        traceDataStore.modelState.artifacts
          .filter((a: Artifact) => a.artifactType.id === event.artifactType.id)
          .map(async (a: Artifact) => {
            await traceDataStore.updateTraceDataFromApi({
              artifactService: artifactService,
              projectId: parseInt(route.params.projectId as string),
              snapshotId: asSnapshotReference(route.params.snapshotId),
              filterId: parseIntUndefined(route.query.filter as string),
              id: a.id,
              direction: 'any',
            })
          }),
      )

      if (event.artifactType.loadedPages === undefined) {
        event.artifactType.loadedPages = 1
      } else {
        event.artifactType.loadedPages++
      }
    } else {
      // Reset query state
      event.artifactType.containerSearchQuery = undefined
      event.artifactType.containerSearchArtifactCount = undefined
      event.artifactType.loadedPages = 0

      // Remove all the search result artifacts from the store to show the original order again
      traceDataStore.setArtifactsForArtifactType(event.artifactType, [])

      await traceDataStore.loadArtifactsCollectionFromApi({
        artifactService: artifactService,
        projectId: parseInt(route.params.projectId as string),
        snapshotId: asSnapshotReference(route.params.snapshotId),
        typeId: event.artifactType.id,
        filterId: parseIntUndefined(route.query.filter as string),
        //include: 'numberOfLinksAsA, numberofLinksAsB',
        sortBy: 'LINK',
        page: 1,
        pageSize: 15,
      })

      await Promise.all(
        traceDataStore.modelState.artifacts
          .filter((a: Artifact) => a.artifactType.id === event.artifactType.id)
          .map(async (a: Artifact) => {
            await traceDataStore.updateTraceDataFromApi({
              artifactService: artifactService,
              projectId: parseInt(route.params.projectId as string),
              snapshotId: asSnapshotReference(route.params.snapshotId),
              filterId: parseIntUndefined(route.query.filter as string),
              id: a.id,
              direction: 'any',
            })
          }),
      )

      event.artifactType.loadedPages++
    }

    graphState?.addToModel(traceDataStore.modelState).layout()
  }

  function registerArtifactTypeContainerSearch(graph: GraphState | undefined) {
    graphState = graph
  }

  useEventBus('searchArtifactsForArtifactType', freezeWrap(tryWrap(handler), rappidContext))

  return { registerArtifactTypeContainerSearch }
}
